import { format } from 'date-fns'

import type { QuoteLineItem } from '@/components/quotes/quote-line-items-table'
import { formatCurrency, formatTaxRateFraction } from '@/lib/currency-utils'
import { getCustomerFacingPaymentTotals } from '@/lib/invoice-payment-totals'
import { calculateLineTotalCents } from '@/lib/line-item-calculations'

interface PaymentSchedule {
  description: string
  dueDate: Date
  amountCents: number
  isLastSchedule?: boolean      // Kept for callers; no longer gates the tip display
  amountWithTipCents?: number   // amountCents + this schedule's tip share (set on every schedule with a share)
}

interface PaymentInfo {
  amountPaidCents: bigint
  amountDueCents: bigint
  scheduleCount: number
  tipAccountingVersion?: number
  tipPaidCents?: bigint
  tipDueCents?: bigint
}

interface FinancialDocumentTotalsBreakdownProps {
  items: QuoteLineItem[]
  /** Preserve persisted amounts in read-only revisions; drafts recalculate. */
  useStoredTotals?: boolean
  subtotalCents: number
  discountAmountCents: number
  discountRatio: number
  taxAmountCents: number
  tipCents?: number
  enforcedTipPercentage?: number
  totalCents: number
  paymentSchedules?: PaymentSchedule[]
  showPaymentInfo?: boolean
  paymentInfo?: PaymentInfo
}

/**
 * FinancialDocumentTotalsBreakdown - Display itemized breakdown of totals
 *
 * Features:
 * - Line items breakdown with quantity and unit price
 * - Subtotal row
 * - Discount row (conditional)
 * - Per-line tax breakdown
 * - Tip row (conditional)
 * - Total row
 * - Payment schedule preview (conditional)
 * - Payment info card (conditional, invoice-only)
 */
export function FinancialDocumentTotalsBreakdown({
  items,
  useStoredTotals = false,
  subtotalCents,
  discountAmountCents,
  discountRatio,
  taxAmountCents,
  tipCents,
  enforcedTipPercentage,
  totalCents,
  paymentSchedules,
  showPaymentInfo = false,
  paymentInfo,
}: FinancialDocumentTotalsBreakdownProps) {
  const customerFacingPaymentTotals = paymentInfo
    ? getCustomerFacingPaymentTotals({ ...paymentInfo, tipCents })
    : undefined
  const getLineTotal = (item: QuoteLineItem) => useStoredTotals && item.lineTotalCents !== undefined
    ? item.lineTotalCents
    : calculateLineTotalCents(item.quantity, item.unitPriceCents)

  return (
    <div className={showPaymentInfo && paymentInfo ? "grid gap-4 lg:grid-cols-[2fr_1fr]" : ""}>
      {/* Left Column: Detailed Breakdown */}
      <div className="space-y-4 rounded-2xl border bg-card p-5">
        <div className="space-y-2">
          {/* Line Items Breakdown */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Line Items</h3>
            {items.map((item, idx) => {
              const itemTotal = getLineTotal(item)
              const unitPrice = formatCurrency(item.unitPriceCents)
              return (
                <div key={idx} className="flex justify-between gap-4 pl-4 text-sm text-muted-foreground">
                  <span className="min-w-0">{item.name} ({item.quantity}x ${unitPrice})</span>
                  <span className="font-financial tabular-nums">${formatCurrency(itemTotal)}</span>
                </div>
              )
            })}
          </div>

          <div className="flex justify-between gap-4 pt-2 text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-financial font-medium tabular-nums">${formatCurrency(subtotalCents)}</span>
          </div>

          <div className="border-t" />

          {discountAmountCents > 0 && (
            <div className="flex justify-between gap-4 text-sm">
              <span className="text-muted-foreground">Discount</span>
              <span className="font-financial font-medium tabular-nums text-status-success-fg">
                -${formatCurrency(discountAmountCents)}
              </span>
            </div>
          )}

          {/* Per-line-item tax breakdown */}
          {items.some(item => item.isTaxable && item.salesTaxRate) && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Sales Tax</h3>
              {items
                .filter(item => item.isTaxable && item.salesTaxRate)
                .map((item, idx) => {
                  const itemTotal = getLineTotal(item)
                  const itemAfterDiscount = Math.round(itemTotal * discountRatio)
                  const itemTax = Math.round(itemAfterDiscount * item.salesTaxRate!)
                  const taxPercentage = formatTaxRateFraction(item.salesTaxRate || 0)
                  return (
                    <div key={idx} className="flex justify-between gap-4 pl-4 text-sm text-muted-foreground">
                      <span className="min-w-0">{item.name} ({taxPercentage}% - {item.salesTaxRateName})</span>
                      <span className="font-financial tabular-nums">${formatCurrency(itemTax)}</span>
                    </div>
                  )
                })}
              <div className="flex justify-between gap-4 pt-2 text-sm">
                <span className="text-muted-foreground">Total Tax</span>
                <span className="font-financial font-medium tabular-nums">${formatCurrency(taxAmountCents)}</span>
              </div>
            </div>
          )}

          {tipCents !== undefined && tipCents > 0 ? (
            <>
              <div className="flex justify-between gap-4 border-t pt-2">
                <span className="font-semibold">Total</span>
                <span className="font-financial font-semibold tabular-nums">${formatCurrency(totalCents)}</span>
              </div>
              <div className="flex justify-between gap-4 text-sm">
                <span className="text-muted-foreground">
                  Tip
                  {enforcedTipPercentage && enforcedTipPercentage > 0 &&
                    ` (${enforcedTipPercentage}% enforced)`}
                </span>
                <span className="font-financial font-medium tabular-nums">${formatCurrency(tipCents)}</span>
              </div>
              <div className="flex justify-between gap-4 border-t-2 border-foreground pt-2">
                <span className="font-semibold">Grand Total</span>
                <span className="font-financial text-xl font-semibold tabular-nums">${formatCurrency(totalCents + tipCents)}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between gap-4 border-t-2 border-foreground pt-2">
              <span className="font-semibold">Total</span>
              <span className="font-financial text-xl font-semibold tabular-nums">${formatCurrency(totalCents)}</span>
            </div>
          )}

          {/* Payment Schedule breakdown */}
          {paymentSchedules && paymentSchedules.length > 0 && (
            <>
              <div className="border-t pt-2" />
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Payment Schedule</h3>
                {paymentSchedules.map((schedule, idx) => (
                  <div key={idx} className="flex justify-between gap-4 pl-4 text-sm text-muted-foreground">
                    <span className="min-w-0">{schedule.description} (Due: {format(schedule.dueDate, 'MMM dd, yyyy')})</span>
                    {schedule.amountWithTipCents != null && schedule.amountWithTipCents > schedule.amountCents ? (
                      <span className="flex items-center gap-2">
                        <span className="font-financial text-xs tabular-nums">
                          ${formatCurrency(schedule.amountCents)} + ${formatCurrency(schedule.amountWithTipCents - schedule.amountCents)} tip
                        </span>
                        <span className="font-financial font-medium tabular-nums">${formatCurrency(schedule.amountWithTipCents)}</span>
                      </span>
                    ) : (
                      <span className="font-financial tabular-nums">${formatCurrency(schedule.amountCents)}</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Column: Payment Status (Invoice only) */}
      {showPaymentInfo && paymentInfo && (
        <div className="h-fit space-y-3 rounded-2xl border bg-card p-5">
          <div className="flex items-center justify-between gap-4 text-sm">
            <span className="text-muted-foreground">Amount Paid</span>
            <span className="font-financial font-semibold tabular-nums">${formatCurrency(customerFacingPaymentTotals?.paidCents ?? 0)}</span>
          </div>
          {(paymentInfo.tipAccountingVersion ?? 0) >= 1 && Number(paymentInfo.tipPaidCents ?? 0n) > 0 && (
            <div className="flex items-center justify-between gap-4 pl-4 text-xs text-muted-foreground">
              <span>incl. tip</span>
              <span className="font-financial tabular-nums">${formatCurrency(Number(paymentInfo.tipPaidCents ?? 0n))}</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-4 text-sm">
            <span className="text-muted-foreground">Amount Due</span>
            <span className="font-financial font-semibold tabular-nums">${formatCurrency(customerFacingPaymentTotals?.dueCents ?? 0)}</span>
          </div>
          {(paymentInfo.tipAccountingVersion ?? 0) >= 1 && Number(paymentInfo.tipDueCents ?? 0n) > 0 && (
            <div className="flex items-center justify-between gap-4 pl-4 text-xs text-muted-foreground">
              <span>incl. tip</span>
              <span className="font-financial tabular-nums">${formatCurrency(Number(paymentInfo.tipDueCents ?? 0n))}</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-4 text-sm">
            <span className="text-muted-foreground">Payment Schedules</span>
            <span className="tabular-nums">{paymentInfo.scheduleCount}</span>
          </div>
        </div>
      )}
    </div>
  )
}
